import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import AeroGuestsDropdown, { GuestCounts } from "./AeroGuestsDropdown";
import { AERO_AIRPORTS } from "../../services/darwinApi";

interface AeroHeaderProps {
    origin?: string;
    destination?: string;
    adults?: number;
    tripType?: "roundtrip" | "oneway";
    onOriginChange?: (code: string) => void;
    onDestinationChange?: (code: string) => void;
    onAdultsChange?: (count: number) => void;
    onTripTypeChange?: (type: "roundtrip" | "oneway") => void;
}

/*
 * All values extracted from the live aero.com via Chrome DevTools MCP.
 * Font: "Haas Grot Text Web", sans-serif (we fall back to system sans-serif)
 */

const GRADIENT_STRIPES = [
    "#a40000",
    "#c51600",
    "#e42c01",
    "#fb6900",
    "#fc9301",
    "#fdc002",
];
const BORDER = "#282828"; // rgb(40,40,40)
const ORANGE = "#fb6900"; // rgb(251,105,0)
const MUTED = "#949494"; // rgb(148,148,148) — lightQuinary
const DIM = "#757575"; // rgb(117,117,117) — darkQuinary

const AeroLogo: React.FC = () => (
    <svg
        xmlns="http://www.w3.org/2000/svg"
        width="55"
        height="20"
        fill="none"
        viewBox="0 0 55 20"
    >
        <path
            fill="#fff"
            fillRule="evenodd"
            d="M11.77 11.63H5.35l2.33-6.58c.39-1.13.91-2.77.91-2.77h.05s.5 1.67.88 2.77l2.25 6.58ZM10 0H7.22L0 19.62h2.52l2.12-5.98h7.82l2.09 5.98h2.68L10 0Zm18.55 11.39h-8.53c.32-2.58 1.75-4.56 4.39-4.56 2.55 0 4 1.73 4.14 4.56ZM24.4 5.08c-4.04 0-6.73 3.34-6.73 7.46 0 4.11 2.53 7.46 6.86 7.46 3.21 0 5.44-1.73 6.1-4.77h-2.17c-.5 1.92-1.87 2.9-3.93 2.9-2.85 0-4.41-2.19-4.58-5.07h10.98c0-4.7-2.28-7.98-6.53-7.98Zm10.92 2.71h.05a5.03 5.03 0 0 1 4.23-2.52c.41 0 .63.05.9.16v2.14h-.08a2.69 2.69 0 0 0-.99-.14c-2.2 0-4.11 1.65-4.11 4.31v7.88h-2.23V5.43h2.23V7.8Zm12.7-.88c3.02 0 4.56 2.5 4.56 5.63s-1.54 5.6-4.56 5.6c-3.02 0-4.58-2.47-4.58-5.6 0-3.13 1.56-5.63 4.58-5.63Zm0-1.83c-4.22 0-6.86 3.34-6.86 7.46 0 4.11 2.64 7.46 6.86 7.46 4.26 0 6.84-3.35 6.84-7.46 0-4.12-2.58-7.46-6.84-7.46Z"
            clipRule="evenodd"
        />
    </svg>
);

/* Chevron: 24x24, from Aero's actual SVG path */
const ChevronDown: React.FC<{
    className?: string;
    style?: React.CSSProperties;
}> = ({ className, style }) => (
    <svg
        className={className}
        style={style}
        width="24"
        height="24"
        viewBox="0 0 48 48"
        fill="currentColor"
    >
        <path d="M24 31 11 21v-4l13 10 13-10v4L24 31Z" />
    </svg>
);

/* Check icon: 20x20 — fills the entire 20x20 knob, matching Aero */
const CheckIcon: React.FC = () => (
    <svg width="20" height="20" viewBox="0 0 48 48" fill="white">
        <path
            fillRule="evenodd"
            d="M38 17.22 20.78 34 10 23.49l2.27-2.22 8.51 8.3L35.73 15 38 17.22Z"
            clipRule="evenodd"
        />
    </svg>
);

const SwapIcon: React.FC = () => (
    <svg width="21" height="21" viewBox="0 0 48 48" fill="currentColor">
        <path d="m41.96 26.1.04-4.13-3.83 3.04v-1.15a13.65 13.65 0 0 0-8.61-12.85 14.27 14.27 0 0 0-8.19-.71c-2.75.57-5.3 1.92-7.3 3.9l-.34.34 2.28 2.26.35-.35a11.24 11.24 0 0 1 5.62-3c2.13-.44 4.32-.25 6.3.54a10.57 10.57 0 0 1 6.64 9.9l-.01 1.16-3.77-2.96-.05 4.11 5.4 4.24 5.47-4.34ZM33.92 33.8l.35-.34-2.28-2.26-.35.35a11.24 11.24 0 0 1-5.62 3c-2.13.44-4.32.25-6.3-.54a10.57 10.57 0 0 1-6.64-9.9l.01-1.16 3.77 2.96.05-4.11-5.4-4.24-5.47 4.34L6 26.03l3.83-3.04v1.15a13.65 13.65 0 0 0 8.61 12.85 14.27 14.27 0 0 0 8.19.71c2.75-.57 5.3-1.92 7.3-3.9Z" />
    </svg>
);

const ArrowRightIcon: React.FC<{ style?: React.CSSProperties }> = ({
    style,
}) => (
    <svg
        style={style}
        width="40"
        height="40"
        viewBox="0 0 48 48"
        fill="currentColor"
    >
        <path
            fillRule="evenodd"
            d="M41 24 28 40h-3.1l11.28-14.58H9v-2.84h27.18L24.9 8h3.12L41 24Z"
            clipRule="evenodd"
        />
    </svg>
);

const AeroHeader: React.FC<AeroHeaderProps> = ({
    origin: originProp,
    destination: destinationProp,
    adults: adultsProp,
    tripType: tripTypeProp,
    onOriginChange,
    onDestinationChange,
    onAdultsChange,
    onTripTypeChange,
}) => {
    const navigate = useNavigate();
    const [origin, setOrigin] = useState(originProp ?? "MCO");
    const [destination, setDestination] = useState(destinationProp ?? "JFK");
    const [guests, setGuests] = useState<GuestCounts>({
        adults: adultsProp ?? 1,
        infants: 0,
        petInSeat: 0,
        petInCarrier: 0,
        serviceAnimal: 0,
    });
    const [tripType, setTripType] = useState<"roundtrip" | "oneway">(
        tripTypeProp ?? "roundtrip",
    );
    const [guestsOpen, setGuestsOpen] = useState(false);
    const [searchHovered, setSearchHovered] = useState(false);
    const [swapRotated, setSwapRotated] = useState(false);
    const guestsRef = useRef<HTMLDivElement>(null);


    useEffect(() => {
        if (originProp !== undefined) setOrigin(originProp);
    }, [originProp]);
    useEffect(() => {
        if (destinationProp !== undefined) setDestination(destinationProp);
    }, [destinationProp]);
    useEffect(() => {
        if (adultsProp !== undefined)
            setGuests((g) => ({ ...g, adults: adultsProp }));
    }, [adultsProp]);
    useEffect(() => {
        if (tripTypeProp !== undefined) setTripType(tripTypeProp);
    }, [tripTypeProp]);

    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (
                guestsRef.current &&
                !guestsRef.current.contains(e.target as Node)
            )
                setGuestsOpen(false);
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () =>
            document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const handleSwap = () => {
        const o = destination,
            d = origin;
        setOrigin(o);
        setDestination(d);
        onOriginChange?.(o);
        onDestinationChange?.(d);
        setSwapRotated((r) => !r);
    };
    const handleGuestsChange = (g: GuestCounts) => {
        setGuests(g);
        onAdultsChange?.(g.adults);
    };
    const handleTripType = (t: "roundtrip" | "oneway") => {
        setTripType(t);
        onTripTypeChange?.(t);
    };
    const handleSearch = () => {
        navigate(
            `/aero/flights?${new URLSearchParams({ origin, destination, adults: String(guests.adults), infants: String(guests.infants), tripType })}`,
        );
    };

    const orig = AERO_AIRPORTS[origin ];
    const dest = AERO_AIRPORTS[destination ];

    return (
        <>
            {/* Dark overlay when guests dropdown is open — fixed fullscreen, z-75, black 50% opacity */}
            {guestsOpen && (
                <div
                    className="fixed inset-0"
                    style={{
                        backgroundColor: "rgba(0, 0, 0, 0.5)",
                        zIndex: 75,
                    }}
                    onClick={() => setGuestsOpen(false)}
                />
            )}
            <header
                className="fixed overflow-visible"
                style={{
                    /* header: fixed, inset-x-2xl(32px), top-lg(24px), h-[96px], rounded-md(12px), border 1px #282828 */
                    zIndex: guestsOpen ? 80 : 20,
                    left: 32,
                    right: 32,
                    top: 24,
                    height: 96,
                    boxSizing: "content-box" as const,
                    backgroundColor: "#000",
                    border: `1px solid ${BORDER}`,
                    borderRadius: 12,
                    fontFamily:
                        '"Haas Grot Text Web", "Helvetica Neue", Helvetica, Arial, sans-serif',
                }}
            >
                {/* Dim overlay on the header itself when dropdown is open */}
                {guestsOpen && (
                    <div
                        className="absolute inset-0 pointer-events-none"
                        style={{
                            backgroundColor: "rgba(0, 0, 0, 0.5)",
                            borderRadius: 12,
                            zIndex: 1,
                        }}
                    />
                )}

                {/* Rainbow gradient bar — absolute, 16px wide, full height, left edge with matching border-radius */}
                <div
                    className="absolute overflow-hidden flex flex-col"
                    style={{
                        width: 16,
                        left: -1,
                        top: -1,
                        bottom: -1,
                        borderTopLeftRadius: 12,
                        borderBottomLeftRadius: 12,
                    }}
                >
                    {GRADIENT_STRIPES.map((c) => (
                        <div
                            key={c}
                            className="flex-1"
                            style={{ backgroundColor: c }}
                        />
                    ))}
                </div>

                {/* Main row: flex, items-end (logo aligns to bottom, sections fill full height) */}
                <div className="flex items-end h-full">
                    {/* ── 1. LOGO ── 87px wide, bottom-aligned, pl-2xl(32px) pb-lg(24px) */}
                    <a
                        href="/aero"
                        className="block"
                        style={{
                            flex: "0 1 auto",
                            width: 87,
                            boxSizing: "content-box" as const,
                        }}
                    >
                        {/* Inner div: pl-2xl(32px) pb-lg(24px) — matches Aero's Icon_svg wrapper */}
                        <div
                            style={{
                                paddingLeft: 32,
                                paddingBottom: 24,
                                boxSizing: "content-box" as const,
                            }}
                        >
                            <AeroLogo />
                        </div>
                    </a>

                    {/* ── Main content — ml-sm(48px) from logo, border-left ── */}
                    <div
                        className="flex-1 flex h-full"
                        style={{
                            marginLeft: 48,
                            borderLeft: `1px solid ${BORDER}`,
                        }}
                    >
                        {/* ── 2+3. FROM / SWAP / TO — grid 1fr auto 1fr ──
                             minWidth: 0 on both the flex item AND the grid
                             tracks (minmax(0, 1fr) instead of bare 1fr) —
                             without both, grid's default auto-sized tracks
                             refuse to shrink below their content's
                             min-content, which is what squished the From/To
                             columns once the account chip took its ~100px. */}
                        <div
                            className="grid min-w-0"
                            style={{
                                flex: "1 1 auto",
                                minWidth: 0,
                                gridTemplateColumns:
                                    "minmax(0, 1fr) auto minmax(0, 1fr)",
                            }}
                        >
                            {/* FROM cell: padding 0 24px 0 16px */}
                            <div
                                className="flex items-center h-full min-w-0"
                                style={{ padding: "0 24px 0 16px" }}
                            >
                                {/* Left: label + city + airport (flex:1) */}
                                <div style={{ flex: "1 1 0%", minWidth: 0 }}>
                                    {/* "From" — 14px, #949494, lh 16px, mb 4px */}
                                    <div
                                        style={{
                                            fontSize: 14,
                                            lineHeight: "16px",
                                            color: MUTED,
                                            marginBottom: 4,
                                        }}
                                    >
                                        From
                                    </div>
                                    {/* City + Airport stacked */}
                                    <div>
                                        {/* City — 28px, white, lh 32px, weight 400 */}
                                        <div
                                            className="truncate"
                                            style={{
                                                fontSize: 28,
                                                fontWeight: 300,
                                                lineHeight: "32px",
                                                color: "#fff",
                                            }}
                                        >
                                            {orig?.city ?? origin}
                                        </div>
                                        {/* Airport — 14px, #949494, lh 20px */}
                                        <div
                                            className="truncate overflow-hidden text-ellipsis"
                                            style={{
                                                fontSize: 14,
                                                lineHeight: "20px",
                                                color: MUTED,
                                            }}
                                        >
                                            {orig?.name ?? ""}
                                        </div>
                                    </div>
                                </div>
                                {/* Right: IATA code — 24px, #949494, lh 28px
                                    (hidden below 2xl — was xl/1280px, but at
                                    exactly 1280 the header has zero spare
                                    width even before the account chip; this
                                    ~45px/cell is the least valuable thing to
                                    keep since the city name already says it) */}
                                <div
                                    className="flex-shrink-0 hidden 2xl:block"
                                    style={{
                                        fontSize: 24,
                                        lineHeight: "28px",
                                        color: MUTED,
                                    }}
                                >
                                    {origin}
                                </div>
                                {/* Chevron button — 24x24, margin 0 8px 0 16px */}
                                <button
                                    className="flex-shrink-0"
                                    style={{
                                        width: 24,
                                        height: 24,
                                        margin: "0 8px 0 16px",
                                        color: MUTED,
                                        padding: 0,
                                        background: "none",
                                        border: "none",
                                    }}
                                >
                                    <ChevronDown style={{ color: MUTED }} />
                                </button>
                            </div>

                            {/* SWAP — 1px divider with overlapping button */}
                            <div
                                className="relative flex items-center"
                                style={{ width: 1, backgroundColor: BORDER }}
                            >
                                <button
                                    onClick={handleSwap}
                                    className="absolute flex items-center justify-center rounded-full transition-transform duration-500"
                                    style={{
                                        /* 29x29, white bg, white border, centered on divider */
                                        width: 29,
                                        height: 29,
                                        backgroundColor: "#fff",
                                        border: "2px solid #fff",
                                        left: "50%",
                                        top: "50%",
                                        transform: `translate(-50%, -50%) rotate(${swapRotated ? 180 : 0}deg)`,
                                        color: "#000",
                                        padding: 2,
                                    }}
                                    title="Swap airports"
                                >
                                    <SwapIcon />
                                </button>
                            </div>

                            {/* TO cell: padding 0 8px 0 32px */}
                            <div
                                className="flex items-center h-full min-w-0"
                                style={{ padding: "0 8px 0 32px" }}
                            >
                                <div style={{ flex: "1 1 0%", minWidth: 0 }}>
                                    <div
                                        style={{
                                            fontSize: 14,
                                            lineHeight: "16px",
                                            color: MUTED,
                                            marginBottom: 4,
                                        }}
                                    >
                                        To
                                    </div>
                                    <div>
                                        <div
                                            className="truncate"
                                            style={{
                                                fontSize: 28,
                                                fontWeight: 300,
                                                lineHeight: "32px",
                                                color: "#fff",
                                            }}
                                        >
                                            {dest?.city ?? destination}
                                        </div>
                                        <div
                                            className="truncate overflow-hidden text-ellipsis"
                                            style={{
                                                fontSize: 14,
                                                lineHeight: "20px",
                                                color: MUTED,
                                            }}
                                        >
                                            {dest?.name ?? ""}
                                        </div>
                                    </div>
                                </div>
                                <div
                                    className="flex-shrink-0 hidden 2xl:block"
                                    style={{
                                        fontSize: 24,
                                        lineHeight: "28px",
                                        color: MUTED,
                                    }}
                                >
                                    {destination}
                                </div>
                                <button
                                    className="flex-shrink-0"
                                    style={{
                                        width: 24,
                                        height: 24,
                                        margin: "0 8px 0 16px",
                                        color: MUTED,
                                        padding: 0,
                                        background: "none",
                                        border: "none",
                                    }}
                                >
                                    <ChevronDown style={{ color: MUTED }} />
                                </button>
                            </div>
                        </div>

                        {/* ── Right panel ── */}
                        <div
                            className="flex flex-row items-center"
                            style={{ flex: "0 1 auto" }}
                        >
                            {/* ── 4. GUESTS — 152px content + 16px pad each side + 1px border each = 186px rendered ── */}
                            <div
                                ref={guestsRef}
                                className="relative flex flex-col justify-center cursor-pointer self-stretch"
                                style={{
                                    boxSizing: "content-box" as const,
                                    minWidth: 152,
                                    padding: "0 16px",
                                    borderLeft: `1px solid ${BORDER}`,
                                    borderRight: `1px solid ${BORDER}`,
                                }}
                                onClick={() => setGuestsOpen((o) => !o)}
                            >
                                {(() => {
                                    const totalPets =
                                        guests.petInSeat + guests.petInCarrier;
                                    const totalGuests =
                                        guests.adults +
                                        guests.infants +
                                        guests.petInSeat;
                                    const summaryLines: string[] = [];
                                    summaryLines.push(
                                        `${guests.adults} ${guests.adults === 1 ? "Adult" : "Adults"}`,
                                    );
                                    if (guests.infants > 0)
                                        summaryLines.push(
                                            `${guests.infants} ${guests.infants === 1 ? "Infant" : "Infants"}`,
                                        );
                                    if (totalPets > 0)
                                        summaryLines.push(
                                            `${totalPets} ${totalPets === 1 ? "Pet" : "Pets"}`,
                                        );

                                    return (
                                        <div className="flex items-center h-full">
                                            {/* Left: Guests label + large number */}
                                            <div className="flex flex-col justify-center">
                                                <div
                                                    style={{
                                                        fontSize: 14,
                                                        lineHeight: "16px",
                                                        color: DIM,
                                                    }}
                                                >
                                                    Guests
                                                </div>
                                                <div
                                                    style={{
                                                        fontSize: 40,
                                                        lineHeight: "48px",
                                                        color: "#fff",
                                                    }}
                                                >
                                                    {totalGuests}
                                                </div>
                                            </div>
                                            {/* Right: summary lines + chevron, vertically centered */}
                                            <div className="flex items-center ml-auto">
                                                <div className="flex flex-col justify-center mr-2">
                                                    {summaryLines.map(
                                                        (line) => (
                                                            <span
                                                                key={line}
                                                                style={{
                                                                    fontSize: 16,
                                                                    lineHeight:
                                                                        "20px",
                                                                    color: DIM,
                                                                    whiteSpace:
                                                                        "nowrap" as const,
                                                                }}
                                                            >
                                                                {line}
                                                            </span>
                                                        ),
                                                    )}
                                                </div>
                                                <ChevronDown
                                                    className={`transition-transform duration-300 ${guestsOpen ? "rotate-180" : ""}`}
                                                    style={{ color: MUTED }}
                                                />
                                            </div>
                                        </div>
                                    );
                                })()}

                                {guestsOpen && (
                                    <AeroGuestsDropdown
                                        guests={guests}
                                        onGuestsChange={handleGuestsChange}
                                        onDone={() => setGuestsOpen(false)}
                                    />
                                )}
                            </div>

                            {/* ── 5. ROUND TRIP / ONE WAY — 152px content-box, padding on inner label ── */}
                            <div
                                className="flex flex-col justify-center self-stretch"
                                style={{
                                    boxSizing: "content-box" as const,
                                    minWidth: 152,
                                    padding: 0,
                                }}
                            >
                                {/* label: flex, items-center, padding 16px 0 16px 20px, w 152px */}
                                <label
                                    className="relative flex items-center cursor-pointer"
                                    style={{
                                        padding: "16px 16px 16px 20px",
                                        width: 152,
                                    }}
                                >
                                    {/* Toggle track: 22x46, rounded-[48px], bg #282828 */}
                                    <div
                                        className="relative flex-shrink-0 overflow-hidden"
                                        style={{
                                            width: 22,
                                            height: 46,
                                            borderRadius: 48,
                                            backgroundColor: BORDER,
                                        }}
                                        onClick={() =>
                                            handleTripType(
                                                tripType === "roundtrip"
                                                    ? "oneway"
                                                    : "roundtrip",
                                            )
                                        }
                                    >
                                        {/* Knob: 20x20, rounded-[46px], bg #ff6a00, absolute */}
                                        <div
                                            className="absolute flex items-center justify-center transition-all duration-400 ease-out"
                                            style={{
                                                width: 20,
                                                height: 20,
                                                borderRadius: 46,
                                                backgroundColor:
                                                    "rgb(255, 106, 0)",
                                                left: 1,
                                                top:
                                                    tripType === "roundtrip"
                                                        ? 1
                                                        : 25,
                                            }}
                                        >
                                            <CheckIcon />
                                        </div>
                                    </div>

                                    {/* Labels */}
                                    <div
                                        className="flex flex-col"
                                        style={{ marginLeft: 12 }}
                                    >
                                        {/* "Round Trip" — 14px, weight 500, lh 14px, py 4px, white when active */}
                                        <div
                                            className="cursor-pointer transition-colors duration-400"
                                            style={{
                                                fontSize: 14,
                                                fontWeight: 500,
                                                lineHeight: "14px",
                                                padding: "4px 0",
                                                color:
                                                    tripType === "roundtrip"
                                                        ? "#fff"
                                                        : DIM,
                                            }}
                                            onClick={() =>
                                                handleTripType("roundtrip")
                                            }
                                        >
                                            Round Trip
                                        </div>
                                        {/* "One Way" — 14px, weight 500, lh 14px, py 4px */}
                                        <div
                                            className="cursor-pointer transition-colors duration-400"
                                            style={{
                                                fontSize: 14,
                                                fontWeight: 500,
                                                lineHeight: "14px",
                                                padding: "4px 0",
                                                color:
                                                    tripType === "oneway"
                                                        ? "#fff"
                                                        : DIM,
                                            }}
                                            onClick={() =>
                                                handleTripType("oneway")
                                            }
                                        >
                                            One Way
                                        </div>
                                    </div>
                                </label>
                            </div>

                            {/* ── 6. SEARCH — 120x80, bg orange, rounded-lg(8px), padding 20px ── */}
                            <div className="flex items-center self-stretch flex-shrink-0">
                                <button
                                    onClick={handleSearch}
                                    onMouseEnter={() => setSearchHovered(true)}
                                    onMouseLeave={() => setSearchHovered(false)}
                                    className="group flex items-center justify-center overflow-hidden transition-all duration-400 cursor-pointer"
                                    style={{
                                        width: 120,
                                        height: 80,
                                        backgroundColor: searchHovered
                                            ? "#fff"
                                            : ORANGE,
                                        borderRadius: 8,
                                        border: "none",
                                        padding: 20,
                                    }}
                                    aria-label="Search flights"
                                >
                                    <ArrowRightIcon
                                        style={{
                                            color: searchHovered
                                                ? ORANGE
                                                : "#fff",
                                        }}
                                    />
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* ── 7. HAMBURGER — px-lg(24px), self-stretch ── */}
                    <div
                        className="flex items-center self-stretch"
                        style={{ padding: "0 24px" }}
                    >
                        <button
                            className="block cursor-pointer"
                            style={{
                                padding: "16px 0",
                                background: "none",
                                border: "none",
                            }}
                            aria-label="Menu"
                        >
                            {/* 3 lines: 22x2 white, 4px gap between */}
                            <span
                                className="block"
                                style={{
                                    width: 22,
                                    height: 2,
                                    backgroundColor: "#fff",
                                    marginBottom: 4,
                                }}
                            />
                            <span
                                className="block"
                                style={{
                                    width: 22,
                                    height: 2,
                                    backgroundColor: "#fff",
                                }}
                            />
                            <span
                                className="block"
                                style={{
                                    width: 22,
                                    height: 2,
                                    backgroundColor: "#fff",
                                    marginTop: 4,
                                }}
                            />
                        </button>
                    </div>
                </div>
            </header>
        </>
    );
};

export default AeroHeader;
